import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
	CUSTOM_PALETTE_ID,
	DEFAULT_CUSTOM_PRIMARY,
	DEFAULT_CUSTOM_SECONDARY,
} from "$/modules/shared/lib/palettes";

export type DesignSystem = "playful" | "clean" | "brutalist" | "soft";
export type AppearanceMode = "light" | "dark" | "system";

type AppearanceState = {
	designSystem: DesignSystem;
	mode: AppearanceMode;
	/** A preset id from PALETTE_PRESETS, or CUSTOM_PALETTE_ID. */
	paletteId: string;
	customPrimary: string;
	customSecondary: string;
	setDesignSystem: (designSystem: DesignSystem) => void;
	setMode: (mode: AppearanceMode) => void;
	setPaletteId: (paletteId: string) => void;
	setCustomPrimary: (hex: string) => void;
	setCustomSecondary: (hex: string) => void;
};

/**
 * Global appearance preference: design system (shape/character), light/dark
 * mode, and a color palette (preset or custom primary/secondary hex).
 *
 * Persisted to localStorage because the look applies app-wide, independent
 * of the currently opened project. The resolved theme class is applied to
 * <html> and the palette is injected as CSS vars by AppearanceSync.
 */
export const useAppearance = create<AppearanceState>()(
	persist(
		(set) => ({
			designSystem: "playful",
			mode: "system",
			paletteId: "ocean",
			customPrimary: DEFAULT_CUSTOM_PRIMARY,
			customSecondary: DEFAULT_CUSTOM_SECONDARY,
			setDesignSystem: (designSystem) => set({ designSystem }),
			setMode: (mode) => set({ mode }),
			setPaletteId: (paletteId) => set({ paletteId }),
			setCustomPrimary: (hex) =>
				set({ customPrimary: hex, paletteId: CUSTOM_PALETTE_ID }),
			setCustomSecondary: (hex) =>
				set({ customSecondary: hex, paletteId: CUSTOM_PALETTE_ID }),
		}),
		{ name: "quizfy-appearance" },
	),
);

export const APPEARANCE_THEMES = [
	"playful-light",
	"playful-dark",
	"clean-light",
	"clean-dark",
	"brutalist-light",
	"brutalist-dark",
	"soft-light",
	"soft-dark",
] as const;

export type AppearanceTheme = (typeof APPEARANCE_THEMES)[number];

/** Compose the concrete theme class from design system + a resolved (light/dark) mode. */
export function composeTheme(
	designSystem: DesignSystem,
	resolvedMode: "light" | "dark",
): AppearanceTheme {
	return `${designSystem}-${resolvedMode}`;
}
